import React from "react";
import { Link } from "react-router-dom";
import {
  Quote,
  ShieldCheck,
  FileText,
  PieChart,
  Megaphone,
  Palette,
  Video,
  Search,
  Newspaper,
  Globe,
  Lightbulb,
  ClipboardCheck,
  Users,
  TrendingUp,
  ArrowRight,
  MapPin,
  Sparkles,
  BarChart3,
  Smartphone,
  Zap,
} from "lucide-react";
import VinesAppPreview from "@/components/VinesAppPreview";

// DMOExtras — DMO-specific sections used on /tourism/dmos.
// Centered on the Maureen Kelley (Nelson County, VA) reference letter, the
// 9 service lines she enumerated, and public-sector procurement readiness.
//
// Each section is exported as a named component so TourismCategoryPage can
// compose them in any order. The default <DMOExtras /> still renders them
// in their original sequence for any other consumers.

export const DMOReferenceSection = () => (
  <section
    className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)] px-5 sm:px-8 py-24"
    data-testid="dmo-reference-section"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Verified Public-Sector Reference</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">
            A formal reference,
            <br />
            <span className="text-[var(--af-lime)]">
              from a peer Director of Tourism.
            </span>
          </h2>
          <div className="surface p-7 inline-flex flex-col mb-6">
            <div className="flex items-baseline gap-3 mb-3">
              <div className="font-display text-7xl text-[var(--af-lime)] leading-none">
                10
              </div>
              <div className="text-2xl text-[var(--af-cream)]/55">/ 10</div>
            </div>
            <div className="font-eyebrow text-xs uppercase tracking-widest text-[var(--af-cream)]/65">
              Performance rating from Nelson County, VA
            </div>
            <div className="dashed-divider my-5" />
            <div className="text-sm text-[var(--af-cream)]/80 leading-relaxed">
              Maureen Kelley, Director of Economic Development & Tourism
              for Nelson County, Virginia, a multi-year client,
              provided this reference in support of our work.
            </div>
          </div>
          <Link
            to="/tourism/case-studies"
            className="btn-lime"
            data-testid="dmo-reference-cta"
          >
            See the full reference letter <ArrowRight size={14} />
          </Link>
        </div>

        <div className="lg:col-span-7 space-y-5">
          <div className="surface p-6" data-testid="dmo-reference-quote-headline">
            <Quote size={18} className="text-[var(--af-lime)] mb-3" />
            <p className="text-base sm:text-lg text-[var(--af-cream)]/90 italic leading-relaxed">
              "Anne Flinn Advertising consistently delivers high-quality
              creative products, strategic guidance, and measurable
              results. They are particularly effective at working with{" "}
              <span className="text-[var(--af-lime)] not-italic font-semibold">
                public-sector organizations
              </span>
              , navigating stakeholder considerations, and developing
              messaging that balances policy objectives with public
              understanding."
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="surface p-6" data-testid="dmo-ref-rehire">
              <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                Would you rehire?
              </div>
              <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                "Absolutely. I would not hesitate to hire them again and
                would strongly recommend them to other organizations."
              </p>
            </div>
            <div className="surface p-6" data-testid="dmo-ref-timeline">
              <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                Did they meet timelines?
              </div>
              <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                "Consistently met project deadlines and often exceeds
                expectations when working under compressed timelines."
              </p>
            </div>
            <div className="surface p-6" data-testid="dmo-ref-strength">
              <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                Greatest strength
              </div>
              <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                "Translate complex issues into clear and compelling
                messages, and develop communication strategies that
                resonate with diverse audiences."
              </p>
            </div>
            <div className="surface p-6" data-testid="dmo-ref-outcomes">
              <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                Outcomes delivered
              </div>
              <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                "Strengthened brand awareness, increased visitor
                engagement, improved marketing effectiveness, and
                navigated changing tourism trends."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const DMOCapabilitiesSection = () => (
  <section
    className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
    data-testid="dmo-capabilities-section"
  >
    <div className="mb-14 max-w-3xl">
      <div className="eyebrow mb-4">DMOs · Full-Service Capabilities</div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight">
        Everything you need from your{" "}
        <span className="text-[var(--af-lime)]">tourism agency</span>.
      </h2>
      <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
        DMOs need more than a media buy. You need a strategic partner who
        can run the full marketing function, from brand to board report.
        Here's everything we deliver, under one roof.
      </p>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[
        { icon: Lightbulb, t: "Strategic Marketing Planning", d: "Annual plans, campaign architecture, and KPI frameworks that align with your board's goals." },
        { icon: Palette, t: "Brand & Destination Identity", d: "Visual identity, voice, and positioning that hold up across campaigns and stakeholder presentations." },
        { icon: Megaphone, t: "Advertising Campaign Creation", d: "Full creative concepting (Wanderlove, Dark Skies, Eat. Drink. Love.) plus the media placement to ship it." },
        { icon: Globe, t: "Multi-State Media Buying", d: "Streaming, PMAX, paid social, and Social Mirror, coordinated across DMAs for stay-over traffic." },
        { icon: Video, t: "Creative & Video Production", d: "On-brand graphic design, 15- and 30-second video spots, and refreshed campaign creative each season." },
        { icon: Newspaper, t: "Public Relations Support", d: "Earned-media strategy, press releases, partner coordination, and Virginia Tourism Corporation alignment." },
        { icon: Search, t: "Digital Marketing & SEO", d: "Landing-page strategy, organic search, and digital infrastructure that compounds campaign reach." },
        { icon: PieChart, t: "Visitor Research & Insights", d: "Audience analysis, demographic targeting, and visitor data that informs next year's plan." },
        { icon: Users, t: "Stakeholder Communications", d: "Messaging for boards, local businesses, county leadership, and Virginia Tourism partners." },
      ].map((d, i) => (
        <div
          key={i}
          className="surface p-6"
          data-testid={`dmo-capability-${i}`}
        >
          <d.icon size={22} className="text-[var(--af-lime)] mb-3" />
          <div className="font-display text-lg mb-2 leading-tight">
            {d.t}
          </div>
          <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
            {d.d}
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const DMOProcurementSection = () => (
  <section
    className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)] px-5 sm:px-8 py-24"
    data-testid="dmo-procurement-section"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Built for Public-Sector Buying</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5 leading-tight">
            RFP-ready.
            <br />
            Board-ready.
            <br />
            <span className="text-[var(--af-lime)]">Grant-ready.</span>
          </h2>
          <p className="text-base text-[var(--af-cream)]/75 leading-relaxed mb-6">
            We've worked with DMOs and public-sector tourism orgs long
            enough to know the buying process, and the reporting
            requirements that come after the contract is signed.
          </p>
          <a
            href="#lead-form"
            className="btn-lime"
            data-testid="dmo-procurement-cta"
          >
            Request references & SOW <ArrowRight size={14} />
          </a>
        </div>

        <div className="lg:col-span-7 space-y-5">
          {[
            {
              icon: FileText,
              t: "RFP & Procurement Documentation",
              d: "We respond to competitive solicitations regularly. References (like the Nelson County 10/10 above), W-9s, statement of work templates, scope-of-services language, and pricing breakdowns, all on file and ready to share.",
            },
            {
              icon: ClipboardCheck,
              t: "Board-Ready Reporting Cadence",
              d: "Monthly performance reports designed for board presentations: brand reach, visitor engagement, multi-state DMA performance, year-over-year delta. Not screenshots, narrative + data.",
            },
            {
              icon: ShieldCheck,
              t: "Stakeholder Communications Expertise",
              d: "We routinely coordinate with county leadership, local business partners, state tourism corporations, and grant-funding entities. We translate policy into public-facing messaging, and vice versa.",
            },
            {
              icon: TrendingUp,
              t: "Grant-Aligned Campaign Strategy",
              d: "Virginia Tourism Corporation Marketing Leverage Program (MLP) grants, ARPA tourism funds, county economic development budgets, we structure campaigns so the spend ties cleanly to the grant requirement.",
              vifl: true,
            },
          ].map((p, i) => (
            <div
              key={i}
              className="surface p-6 flex gap-4 items-start"
              data-testid={`dmo-procurement-${i}`}
            >
              <div className="w-11 h-11 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
                <p.icon size={20} className="text-[var(--af-lime)]" />
              </div>
              <div>
                <div className="font-display text-lg mb-2">{p.t}</div>
                <div className="text-sm text-[var(--af-cream)]/75 leading-relaxed">
                  {p.d}
                </div>
                {p.vifl && (
                  <div
                    className="mt-4 pt-4 border-t border-[var(--af-line)]/60 flex items-center gap-4"
                    data-testid="dmo-procurement-vifl-badge"
                  >
                    <img
                      src="/media/clients/vifl-horizontal.png"
                      alt="Virginia Is For Lovers"
                      className="h-5 w-auto opacity-90 flex-shrink-0"
                    />
                    <div className="text-[10px] uppercase tracking-widest text-[var(--af-cream)]/65 leading-snug">
                      VIFL-compliant creative · ready for grant reporting
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const DMOVinesCallout = () => (
  <section
    className="relative px-5 sm:px-8 py-20 overflow-hidden border-y border-[var(--af-line)] bg-[var(--af-navy-deep)]"
    data-testid="dmo-vines-callout"
  >
    {/* Decorative grid + soft lime glow */}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(var(--af-lime) 1px, transparent 1px), linear-gradient(90deg, var(--af-lime) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    />
    <div
      aria-hidden="true"
      className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(closest-side, rgba(184,255,82,0.18), transparent 70%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--af-lime)]/40 bg-[var(--af-lime)]/10 mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
        <span className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)]">
          Exclusive to Anne Flinn Advertising · The Platform Play
        </span>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start mb-10">
        <div className="lg:col-span-7">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
            Your{" "}
            <span className="text-[var(--af-lime)]">custom destination app</span>
            , ready in weeks, branded as yours.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-base sm:text-lg text-[var(--af-cream)]/80 leading-relaxed">
            A turnkey mobile app, interactive map, and branded directory
            built specifically for your destination. Our proven platform,{" "}
            <span className="text-[var(--af-cream)] font-semibold">
              vines.app
            </span>
            , launched in 2024 and now powers 280+ Virginia wineries. We
            ship the same engine, customized for your DMO, in 6–10 weeks.
          </p>
        </div>
      </div>

      {/* Live engine credibility badge — sits above the live map */}
      <div
        className="flex items-center gap-2 mb-5"
        data-testid="dmo-vines-live-badge"
      >
        <span
          className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)] animate-pulse"
          aria-hidden="true"
        />
        <span className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)]">
          Live · vines.app engine
        </span>
      </div>

      {/* Map + phone visible immediately, no second intro */}
      <VinesAppPreview hideIntro className="px-0 py-0" />

      {/* Speed badge — bridges the live engine demo to the customization grid */}
      <div
        className="flex items-center gap-3 text-xs uppercase tracking-widest text-[var(--af-cream)]/65 mt-10 mb-8"
        data-testid="dmo-vines-speed-badge"
      >
        <Zap size={14} className="text-[var(--af-lime)]" />
        <span>6–10 weeks from kickoff to App Store, branded as yours</span>
      </div>

      {/* Customization grid — what "custom" actually means for a DMO */}
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12"
        data-testid="dmo-vines-feature-grid"
      >
        {[
          {
            icon: Palette,
            t: "Your branding",
            d: "Your logo, colors, fonts, App Store listing, and home-screen icon. Visitors download your app, not a generic skin.",
          },
          {
            icon: MapPin,
            t: "Your partners",
            d: "Restaurants, hotels, wineries, breweries, attractions, and events, curated and mapped exactly for your region.",
          },
          {
            icon: Sparkles,
            t: "Custom features",
            d: "Trail check-ins, gamified passports, deals + offers, push notifications, seasonal collections. You pick the modules.",
          },
          {
            icon: Search,
            t: "SEO + custom domain",
            d: "Your-region.app or a branded subdomain on vines.app. Indexed pages for every partner so search traffic comes to you.",
          },
          {
            icon: BarChart3,
            t: "First-party analytics",
            d: "Visitor counts, partner clicks, trail completions, deal redemptions. Dashboard-ready data for your board report.",
          },
          {
            icon: Smartphone,
            t: "iOS + web in one ship",
            d: "One platform, two surfaces. No second project, no second invoice. Both go live the same day.",
          },
        ].map((f, i) => (
          <div
            key={i}
            className="surface p-5"
            data-testid={`dmo-vines-feature-${i}`}
          >
            <f.icon size={20} className="text-[var(--af-lime)] mb-3" />
            <div className="font-display text-lg mb-2 leading-tight">
              {f.t}
            </div>
            <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
              {f.d}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-10">
        <Link
          to="/platforms"
          className="btn-lime"
          data-testid="dmo-vines-cta-platforms"
        >
          <MapPin size={14} /> See what we can build
        </Link>
        <a
          href="https://vines.app"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
          data-testid="dmo-vines-cta-external"
        >
          View vines.app live <ArrowRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

const DMOExtras = () => (
  <>
    <DMOReferenceSection />
    <DMOCapabilitiesSection />
    <DMOProcurementSection />
    <DMOVinesCallout />
  </>
);

export default DMOExtras;
